import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReportTable from "../ReportTable";
import { principleTitles, renderYesNo, safeText } from "./helpers";
import usePrincipleData from "./usePrincipleData";
import { get } from "../../../utils/api";

const getFinancialYearLabels = (financialYear) => {
  const year = String(financialYear || "").trim();

  if (!year) {
    return {
      currentYear: "",
      previousYear: "",
    };
  }

  const numericYear = Number(year);
  if (!Number.isNaN(numericYear)) {
    return {
      currentYear: year,
      previousYear: String(numericYear - 1),
    };
  }

  const rangeMatch = year.match(/(\d{4})\s*-\s*(\d{2,4})/);
  if (rangeMatch) {
    const startYear = Number(rangeMatch[1]);
    return {
      currentYear: year,
      previousYear: `${startYear - 1}-${String(startYear).slice(-2)}`,
    };
  }

  return {
    currentYear: year,
    previousYear: "",
  };
};

const Principle2 = ({ styles }) => {
  const { id } = useParams();
  const { loading, principle } = usePrincipleData(2);
  const [reportingFinancialYear, setReportingFinancialYear] = useState("");
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const lcaEnabled = ["true", "yes"].includes(
    String(leadership?.lifeCycleAssessments?.status).toLowerCase(),
  );

  useEffect(() => {
    const fetchReportingYear = async () => {
      if (!id) return;

      try {
        const response = await get(`/brsr/get/list?page=1&limit=10`);
        const brsrList = [
          response?.data?.data,
          response?.data?.data?.data,
          response?.data?.BRSR,
        ].find(Array.isArray);
        const currentReport =
          brsrList?.find((item) => String(item?._id) === String(id)) || null;

        setReportingFinancialYear(currentReport?.year || "");
      } catch (error) {
        console.log(error);
        setReportingFinancialYear("");
      }
    };

    fetchReportingYear();
  }, [id]);

  const q1Rows = [
    {
      _id: "p2-rd",
      category: "R&D",
      current: safeText(essential?.rAndD?.currentFinancialYear, "N/A"),
      previous: safeText(essential?.rAndD?.previousFinancialYear, "N/A"),
      details: safeText(essential?.rAndD?.detailsOfImprovements, "N/A"),
    },
    {
      _id: "p2-capex",
      category: "Capex",
      current: safeText(essential?.CapexInvestments?.currentFinancialYear, "N/A"),
      previous: safeText(essential?.CapexInvestments?.previousFinancialYear, "N/A"),
      details: safeText(essential?.CapexInvestments?.detailsOfImprovements, "N/A"),
    },
  ];

  const q2Rows = (Array.isArray(leadership?.significantRisksFromLCA)
    ? leadership.significantRisksFromLCA
    : []
  ).map((row, idx) => ({
    _id: `p2-l2-${idx}`,
    productName: safeText(row?.productName, "N/A"),
    description: safeText(row?.descriptionOfRisk, "N/A"),
    action: safeText(row?.actionTaken, "N/A"),
  }));

  const q1AssessmentRows = (Array.isArray(leadership?.lifeCycleAssessments?.assessments)
    ? leadership.lifeCycleAssessments.assessments
    : []
  ).map((row, idx) => ({
    _id: `p2-l1-${idx}`,
    nicCode: safeText(row?.nicCode, "N/A"),
    productName: safeText(row?.productName, "N/A"),
    turnover: safeText(row?.percentageOfTurnover, "N/A"),
    boundary: safeText(row?.boundary, "N/A"),
    external: renderYesNo(row?.conductedByExternalAgency),
    publicDomain: renderYesNo(row?.resultsPublicDomain),
    webLink: safeText(row?.webLink, "N/A"),
  }));

  const q3Rows = (Array.isArray(leadership?.recycledOrReusedMaterial)
    ? leadership.recycledOrReusedMaterial
    : []
  ).map((row, idx) => ({
    _id: `p2-l3-${idx}`,
    material: safeText(row?.indicateInputMaterial, "N/A"),
    current: safeText(row?.currentFinancialYear, "N/A"),
    previous: safeText(row?.previousFinancialYear, "N/A"),
  }));

  const q4Rows = [
    ["Plastics (include packaging)", "plastics"],
    ["E-Waste", "eWaste"],
    ["Hazardous Waste", "hazardousWaste"],
    ["Other Waste", "otherWaste"],
  ].map(([category, key], idx) => ({
    _id: `p2-l4-${idx}`,
    category,
    currentReused: safeText(leadership?.reclaimedProductsAndPackaging?.currentFinancialYear?.[key]?.reUsed, "N/A"),
    currentRecycled: safeText(
      leadership?.reclaimedProductsAndPackaging?.currentFinancialYear?.[key]?.recycled,
      "N/A",
    ),
    currentDisposed: safeText(
      leadership?.reclaimedProductsAndPackaging?.currentFinancialYear?.[key]?.safelyDisposed,
      "N/A",
    ),
    previousReused: safeText(leadership?.reclaimedProductsAndPackaging?.previousFinancialYear?.[key]?.reUsed, "N/A"),
    previousRecycled: safeText(
      leadership?.reclaimedProductsAndPackaging?.previousFinancialYear?.[key]?.recycled,
      "N/A",
    ),
    previousDisposed: safeText(
      leadership?.reclaimedProductsAndPackaging?.previousFinancialYear?.[key]?.safelyDisposed,
      "N/A",
    ),
  }));

  const q5Rows = (Array.isArray(leadership?.reclaimedProductsPercentage)
    ? leadership.reclaimedProductsPercentage
    : []
  ).map((row, idx) => ({
    _id: `p2-l5-${idx}`,
    category: safeText(row?.productCategory, "N/A"),
    percentage: safeText(row?.percentage, "N/A"),
  }));
  const reportingYearLabels = getFinancialYearLabels(reportingFinancialYear);

  return (
    <div className={styles.principleBlock}>
      <div className={styles.principleHeading}>{`PRINCIPLE 2: ${principleTitles[1]}`}</div>
      {loading ? <div className={styles.meta}>Loading principle 2...</div> : null}
      <div className={styles.band}>I. ESSENTIAL INDICATOR</div>
      <div className={styles.question}>1. Percentage of R&D and capital expenditure (capex) investments in specific technologies to improve the environmental and social impacts of product and processes to total R&D and capex investments made by the entity, respectively.</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "" },
          { field: "current", label: "Current Financial Year" },
          { field: "previous", label: "Previous Financial Year" },
          { field: "details", label: "Details of improvements in environmental and social impacts" },
        ]}
        rows={q1Rows}
      />
      <div className={styles.question}>
        2. a. Does the entity have procedures in place for sustainable sourcing?
      </div>
      <div className={styles.answer}>{renderYesNo(essential?.sustainableSourcing?.status)}</div>
      {["true", "yes"].includes(String(essential?.sustainableSourcing?.status).toLowerCase()) ? (
        <>
          <div className={styles.question}>
            2. b. If yes, what percentage of inputs were sourced sustainably?
          </div>
          <div className={styles.answer}>{safeText(essential?.sustainableSourcing?.remark, "N/A")}</div>
        </>
      ) : null}
      <div className={styles.question}>
        3. Describe the processes in place to safely reclaim your products for reusing, recycling
        and disposing at the end of life, for (a) Plastics (including packaging) (b) E-waste (c)
        Hazardous waste and (d) other waste.
      </div>
      <ul className={styles.bulletList}>
        <li className={styles.answer}>
          <span className={styles.bulletHeading}>Plastics (including packaging)</span>
          <div  style={{marginTop: "10px"}}>{safeText(essential?.productReclaimProcesses?.plastics, "N/A")}</div>
        </li>
        <li className={styles.answer}>
          <span className={styles.bulletHeading}>E-Waste</span>
          <div style={{marginTop: "10px"}}>{safeText(essential?.productReclaimProcesses?.eWaste, "N/A")}</div>
        </li>
        <li className={styles.answer}>
          <span className={styles.bulletHeading}>Hazardous Waste</span>
          <div style={{marginTop: "10px"}}>{safeText(essential?.productReclaimProcesses?.hazardousWaste, "N/A")}</div>
        </li>
        <li className={styles.answer}>
          <span className={styles.bulletHeading}>Other Waste</span>
          <div style={{marginTop: "10px"}}>{safeText(essential?.productReclaimProcesses?.otherWaste, "N/A")}</div>
        </li>
      </ul>

      <div className={styles.question}>
        4. a. Whether Extended Producer Responsibility (EPR) is applicable to the entity's
        activities.
      </div>
      <div className={styles.answer}>{renderYesNo(essential?.eprApplicable)}</div>
      {["true", "yes"].includes(String(essential?.eprApplicable).toLowerCase()) ? (
        <>
          <div className={styles.question}>
            4. b. If yes, whether the waste collection plan is in line with the Extended Producer
            Responsibility (EPR) plan submitted to Pollution Control Boards.
          </div>
          <div className={styles.answer}>{safeText(essential?.eprWasteCollectionPlan?.remark, "N/A")}</div>
        </>
      ) : null}

      <div className={styles.band}>II. LEADERSHIP INDICATOR</div>
      <div className={styles.question}>
        1. Has the entity conducted Life Cycle Perspective / Assessments (LCA) for any of its
        products (for manufacturing industry) or for its services (for service industry) (Yes / No)?
        If yes, provide details:
      </div>
      <div className={styles.answer}>{renderYesNo(leadership?.lifeCycleAssessments?.assessments?.length > 0)}</div>
      {lcaEnabled ? (
        <ReportTable
          columns={[
            { field: "nicCode", label: "NIC Code" },
            { field: "productName", label: "Name of Product / Service" },
            { field: "turnover", label: "% of total Turnover contributed" },
            { field: "boundary", label: "Boundary for which LCA was conducted" },
            { field: "external", label: "Conducted by independent external agency" },
            { field: "publicDomain", label: "Results communicated in public domain" },
            { field: "webLink", label: "Web link" },
          ]}
          rows={q1AssessmentRows}
        />
      ) : null}
      <div className={styles.question}>
        2. If there are any significant social or environmental concerns and/or risks arising from
        production or disposal of your products / services, as identified in the Life Cycle
        Perspective / Assessments (LCA) or through any other means, briefly describe the same
        along-with action taken to mitigate the same.
      </div>
      <ReportTable
        columns={[
          { field: "productName", label: "Name of Product / Service" },
          { field: "description", label: "Description of risk / concern" },
          { field: "action", label: "Action Taken" },
        ]}
        rows={q2Rows}
      />
      <div className={styles.question}>
        3. Percentage of recycled or reused input material to total material (by value) used in
        production (for manufacturing industry) or providing services (for service industry).
      </div>
      <ReportTable
        columns={[
          { field: "material", label: "Indicate input material" },
          { field: "current", label: "Percentage in Current Financial Year" },
          { field: "previous", label: "Percentage in Previous Financial Year" },
        ]}
        rows={q3Rows}
      />
      <div className={`${styles.question} ${styles.boldAnswer}`}>
        4. Of the products and packaging reclaimed at end of life of products, amount (in metric
        tonnes) reused, recycled, and safely disposed:
      </div>
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        a. Current Financial Year
        {reportingYearLabels.currentYear
          ? ` (${reportingYearLabels.currentYear})`
          : ""}
      </div>
      <ReportTable
        columns={[
          { field: "category", label: "Category" },
          { field: "reused", label: "Reused" },
          { field: "recycled", label: "Recycled" },
          { field: "disposed", label: "Safely Disposed" },
        ]}
        rows={q4Rows.map((row) => ({
          _id: `${row._id}-current`,
          category: row.category,
          reused: row.currentReused,
          recycled: row.currentRecycled,
          disposed: row.currentDisposed,
        }))}
      />
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        b. Previous Financial Year
        {reportingYearLabels.previousYear
          ? ` (${reportingYearLabels.previousYear})`
          : ""}
      </div>
      <ReportTable
        columns={[
          { field: "category", label: "Category" },
          { field: "reused", label: "Reused" },
          { field: "recycled", label: "Recycled" },
          { field: "disposed", label: "Safely Disposed" },
        ]}
        rows={q4Rows.map((row) => ({
          _id: `${row._id}-previous`,
          category: row.category,
          reused: row.previousReused,
          recycled: row.previousRecycled,
          disposed: row.previousDisposed,
        }))}
      />
      <div className={styles.question}>
        5. Reclaimed products and their packaging materials (as percentage of products sold) for
        each product category.
      </div>
      <ReportTable
        columns={[
          { field: "category", label: "Indicate product category" },
          {
            field: "percentage",
            label: "Reclaimed products and packaging as % of total products sold",
          },
        ]}
        rows={q5Rows}
      />
      <div className={styles.pageBreak} />
    </div>
  );
};

export default Principle2;
